/**
 * Created on 2025-06-13
 */
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { withStyles } from '@material-ui/core'
import { connect } from 'react-redux'
import SubmitButton from '../../../../uiKit/buttons/SubmitButton'
import LoaderSmall from '../../../../uiKit/loaders/loaderSmall'
import Heading from '../../../../uiKit/texts/Heading'
import TwoFactorToggle from './TwoFactorToggle'
import TwoFactorConfirmModal from './TwoFactorConfirmModal'
import Modal from '../../../../uiKit/Modal'
import { getTwoFactorStatus, updateTwoFactorStatus } from '../../api/settings'

const styles = theme => ({
  paragraph: {
    fontFamily: 'Lato',
    fontSize: 14,
    color: 'var(--color-text-secondary)',
  },
  container: {
    maxWidth: 927,
    minHeight: 675,
  },
  toggleContainer: {
    marginTop: 30,
    marginBottom: 30,
  },
  customModalPaper: {
    padding: '0px!important',
    height: '246px!important',
    width: '540px!important',
  },
  formControl: {
    marginBottom: 16,
  },
  subHeading: {
    fontFamily: 'Lato',
    fontSize: 16,
    fontWeight: 'bold',
    color: 'var(--color-text-primary)',
    marginTop: 24,
    marginBottom: 16,
  },
})

const TwoFactorAuth = ({ classes, activeBot }) => {
  // State hooks to replace class component state
  const [loading, setLoading] = useState(true)
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingToggleState, setPendingToggleState] = useState(false)

  // useEffect to fetch the current 2FA status from the API
  useEffect(() => {
    if (!activeBot || !activeBot.id) {
      setLoading(false)
      return
    }
    fetchTwoFactorStatus()
  }, [activeBot]) // Dependency on activeBot means this runs when the bot changes

  const fetchTwoFactorStatus = () => {
    getTwoFactorStatus(activeBot.id)
      .then(response => {
        if (response) {
          setTwoFactorEnabled(response.mfaEnabled || false)
        } else {
          setTwoFactorEnabled(false)
        }
      })
      .catch(error => {
        console.error('Error fetching two-factor status:', error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  // Event handlers
  const handleToggle = () => {
    // Instead of immediately toggling, show confirmation modal
    setShowConfirmModal(true)
    setPendingToggleState(!twoFactorEnabled)
  }

  const handleConfirmToggle = () => {
    // User confirmed the toggle action
    setShowConfirmModal(false)
    setSaving(true)

    // Make actual API call
    updateTwoFactorStatus(activeBot.id, pendingToggleState)
      .then(result => {
        console.log('Two-factor update result:', result)
        setTwoFactorEnabled(pendingToggleState)
      })
      .catch(error => {
        console.error('Error updating two-factor status:', error)
        // Revert to previous state on error
        setPendingToggleState(!pendingToggleState)
      })
      .finally(() => {
        setSaving(false)
      })
  }

  const handleCancelToggle = () => {
    // User canceled the toggle action
    setShowConfirmModal(false)
    setPendingToggleState(twoFactorEnabled)
  }

  return (
    <div>
      <div className={classes.container}>
        <Heading>Two Factor Authentication</Heading>
        <p className={classes.paragraph}>
          Add an extra layer of security to user accounts by requiring a second verification step during login.
        </p>

        {loading ? (
          <div
            style={{
              minHeight: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <LoaderSmall showLoader />
          </div>
        ) : (
          <div className={classes.toggleContainer}>
            <TwoFactorToggle enabled={twoFactorEnabled} onChange={handleToggle} />
            {saving && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                <LoaderSmall showLoader />
              </div>
            )}
          </div>
        )}

        <Modal
          open={showConfirmModal}
          onClose={handleCancelToggle}
          disableClose={false}
          classes={{ paper: classes.customModalPaper }}>
          <TwoFactorConfirmModal
            isEnabling={pendingToggleState}
            onConfirm={handleConfirmToggle}
            onCancel={handleCancelToggle}
          />
        </Modal>
      </div>
    </div>
  )
}

TwoFactorAuth.propTypes = {
  classes: PropTypes.object.isRequired,
  activeBot: PropTypes.object.isRequired,
}

const mapStateToProps = state => ({
  activeBot: state.activeBot,
})

export default withRouter(withStyles(styles, { withTheme: true })(connect(mapStateToProps)(TwoFactorAuth)))
